import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { map, Observable, shareReplay, switchMap, tap } from "rxjs";
import { Tutorial, TutorialStep } from "../models";
import { environment } from "#environments/environment";

/**
 * Service for managing tutorials and tutorial steps.
 * Includes response caching to minimize redundant API calls.
 */
@Injectable({
  providedIn: 'root'
})
export class TutorialService {
  private readonly apiUrl = `${environment.apiEndpoint}/tutorials`;

  /** Cached observable for tutorials list - avoids repeated API calls */
  private tutorialsCache$: Observable<Tutorial[]> | null = null;

  constructor(private readonly http: HttpClient) {}

  /**
   * Fetches all tutorials with caching.
   * Subsequent calls return the cached response until invalidated.
   */
  getAllTutorials(): Observable<Tutorial[]> {
    if (!this.tutorialsCache$) {
      this.tutorialsCache$ = this.http.get<Tutorial[]>(this.apiUrl).pipe(
        map(tutorials => tutorials.map(t => this.withSortedSteps(t))),
        shareReplay(1)
      );
    }
    return this.tutorialsCache$;
  }

  /** Fetches a single tutorial by ID */
  getTutorialById(id: number): Observable<Tutorial> {
    return this.http.get<Tutorial>(`${this.apiUrl}/${id}`).pipe(
      map(tutorial => this.withSortedSteps(tutorial))
    );
  }

  /** Creates a new tutorial and invalidates the cache */
  createTutorial(tutorial: Partial<Tutorial>): Observable<Tutorial> {
    return this.http.post<Tutorial>(this.apiUrl, tutorial).pipe(
      tap(() => this.clearCache())
    );
  }

  /** Adds a step to an existing tutorial and invalidates the cache */
  addStepToTutorial(tutorialId: number, step: Partial<TutorialStep>, imageFile?: File | null): Observable<TutorialStep> {
    const formData = this.buildStepFormData(step, imageFile);
    return this.http.post<TutorialStep>(`${this.apiUrl}/${tutorialId}/steps`, formData).pipe(
      tap(() => this.clearCache())
    );
  }

  /** Updates an existing tutorial and invalidates the cache */
  updateTutorial(id: number, tutorial: Partial<Tutorial>): Observable<Tutorial> {
    return this.http.put<Tutorial>(`${this.apiUrl}/${id}`, tutorial).pipe(
      tap(() => this.clearCache())
    );
  }

  /** Deletes a tutorial and invalidates the cache */
  deleteTutorial(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`).pipe(
      tap(() => this.clearCache())
    );
  }

  /**
   * Toggles a tutorial's published visibility and invalidates the cache.
   * When `isPublished` is true the tutorial becomes visible to all viewers;
   * when false it stays admin-only. Restricted to system admins by the API.
   */
  toggleTutorialVisibility(id: number, isPublished: boolean): Observable<Tutorial> {
    return this.http.patch<Tutorial>(`${this.apiUrl}/${id}/visibility`, { IsPublished: isPublished }).pipe(
      tap(() => this.clearCache())
    );
  }

  /** Updates an existing step and invalidates the cache */
  updateStep(tutorialId: number, stepId: number, step: Partial<TutorialStep>, imageFile?: File | null): Observable<TutorialStep> {
   
    if (!imageFile && step.ImageUrl) {
      const parts = step.ImageUrl.split('/');
      const fileName = parts[parts.length - 1];
      const existingImageApiUrl = `${this.apiUrl}/${tutorialId}/steps/${stepId}/image/${encodeURIComponent(fileName)}`;

      return this.http.get(existingImageApiUrl, { responseType: 'blob' }).pipe(
        switchMap(blob => {
          const existingFile = new File([blob], fileName, { type: blob.type || 'image/jpeg' });
          const formData = this.buildStepFormData(step, existingFile);
          return this.http.put<TutorialStep>(`${this.apiUrl}/${tutorialId}/steps/${stepId}`, formData);
        }),
        tap(() => this.clearCache())
      );
    }

    // New image selected or no image at all — send as-is
    const formData = this.buildStepFormData(step, imageFile);
    return this.http.put<TutorialStep>(`${this.apiUrl}/${tutorialId}/steps/${stepId}`, formData).pipe(
      tap(() => this.clearCache())
    );
  }

  /** Deletes a step and invalidates the cache */
  deleteStep(tutorialId: number, stepId: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${tutorialId}/steps/${stepId}`).pipe(
      tap(() => this.clearCache())
    );
  }

  /**
   * Persists a new step display order for a tutorial and invalidates the cache.
   * Only the ordered step IDs are sent — step content and images are untouched.
   */
  reorderSteps(tutorialId: number, orderedStepIds: number[]): Observable<void> {
    return this.http.put<void>(`${this.apiUrl}/${tutorialId}/steps/reorder`, { OrderedStepIds: orderedStepIds }).pipe(
      tap(() => this.clearCache())
    );
  }

  /** Fetches a single step by ID */
  getStepById(tutorialId: number, stepId: number): Observable<TutorialStep> {
    return this.http.get<TutorialStep>(`${this.apiUrl}/${tutorialId}/steps/${stepId}`);
  }

  /**
   * Builds the full image URL for a step using the correct API endpoint:
   */
  getStepImageUrl(step: TutorialStep): string {
    if (!step?.ImageUrl) { return ''; }
    // ImageUrl format: "tutorials/{tutorialId}/{fileName}"
    const parts = step.ImageUrl.split('/');
    const fileName = parts[parts.length - 1]; 
    return `${this.apiUrl}/${step.TutorialID}/steps/${step.ID}/image/${encodeURIComponent(fileName)}`;
  }

  /** Clears the cached tutorials list so the next call fetches fresh data */
  clearCache(): void {
    this.tutorialsCache$ = null;
  }

  /**
   * Returns a copy of the tutorial with its steps ordered by the server-assigned
   * OrderIndex, when present. If the API doesn't send OrderIndex, the API's own
   * order is preserved so a backend-ordered response is never re-sorted.
   */
  private withSortedSteps(tutorial: Tutorial): Tutorial {
    if (!tutorial?.Steps?.length) { return tutorial; }
    const hasOrderIndex = tutorial.Steps.some(s => s.OrderIndex != null);
    if (!hasOrderIndex) { return tutorial; }
    const Steps = [...tutorial.Steps].sort((a, b) => (a.OrderIndex ?? 0) - (b.OrderIndex ?? 0));
    return { ...tutorial, Steps };
  }

  /** Builds a FormData object from step fields and optional image */
  private buildStepFormData(step: Partial<TutorialStep>, imageFile?: File | null): FormData {
    const formData = new FormData();
    if (step.StepNumber != null) { formData.append('StepNumber', step.StepNumber.toString()); }
    if (step.Title != null)      { formData.append('Title', step.Title); }
    if (step.Body != null)       { formData.append('Body', step.Body); }
    // Steps are always active — the "Active" toggle was removed from the UI, but the
    // backend still uses IsActive to determine visibility, so send true to keep new
    // and edited steps visible. Remove once the backend drops the IsActive field.
    formData.append('IsActive', 'true');
    // Always include ImageUrl so backend never receives null for existing images
    formData.append('ImageUrl', step.ImageUrl ?? '');
    if (imageFile) { formData.append('Image', imageFile, imageFile.name); }
    return formData;
  }
}
